import { tint } from 'polished';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import { amountThemeTwo } from '../../../components/_ThemeTwo_';

import { defaultDatePicker, defaultModal, IThemeInterface } from './common';

// amount colors:
export const colorBlackberry: string = '#022a3a';
export const colorBlackberry80: string = '#082939';
export const colorBlackberry70: string = '#4a6672';
export const colorBlackberry60: string = '#677f89';
export const colorBlackberry45: string = '#919EA3';
export const colorBlackberry20: string = '#ccd4d8';
export const colorBlackberry05: string = '#f2f4f5';
export const colorPomegranate: string = '#e91c29';
export const colorPomegranate70: string = '#ef6069';
export const colorPomegranate40: string = '#f6a4a9';
export const colorCoconut: string = '#f2f2f2';
export const colorCoconut50: string = '#f8f8f8';
export const colorCoconut40: string = '#fafafa';
export const colorAvocado: string = '#7befb2';
export const colorMango: string = '#ffd22a';
export const colorWhite: string = '#fff';
export const colorSlate10: string = colorBlackberry20;

const headerBackgroundTint: number = 0.95;
const tableFilterBackgroundTint: number = 0.9;

export const amountTheme: IThemeInterface = {
  name: 'amount',
  outline: `2px solid #7befb2`,
  globalAnchorDecoration: 'none',
  colorBackground: colorCoconut50,
  colorLineHeight: '2.2rem',
  workflowLinkTextDecoration: 'none',

  colorWhite,
  colorSlate5: colorBlackberry05,
  colorSlate10: colorBlackberry20,
  colorSlate20: colorBlackberry20,
  colorSlate50: colorBlackberry45,
  colorSlate70: colorBlackberry60,
  colorSlate: colorBlackberry70,
  colorBlack: colorBlackberry,

  actionButton: {
    colorText: colorBlackberry,
    colorTextHover: colorBlackberry,
    colorTextActive: colorWhite,
    colorBorder: colorBlackberry20,
    colorBorderHover: colorBlackberry,
    colorBackgroundActive: colorBlackberry
  },
  alert: {
    borderWidth: '1px',
    alert: {
      colorBackground: colorMango,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    success: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    primary: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    secondary: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    warning: {
      colorBackground: colorMango,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    inactive: {
      colorBackground: colorCoconut40,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    },
    default: {
      colorBackground: colorCoconut40,
      colorText: colorBlackberry,
      colorBorder: 'transparent',
    }
  },
  banner: {
    alert: {
      colorBorder: 'transparent',
      colorBackground: colorMango,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    success: {
      colorBorder: 'transparent',
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    primary: {
      colorBorder: 'transparent',
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    secondary: {
      colorBorder: 'transparent',
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    warning: {
      colorBorder: 'transparent',
      colorBackground: colorMango,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    inactive: {
      colorBorder: 'transparent',
      colorBackground: colorCoconut50,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    default: {
      colorBorder: 'transparent',
      colorBackground: colorCoconut50,
      colorText: colorBlackberry,
      colorLink: colorBlackberry,
    },
    linkTextWeight: '700',
    sectionBackground: '#ebedee',
    paddingBanner: '0.75em',
    fontSizeBannerText: '16px',
    fontWeightBanner: '600',
  },
  brand: {
    colorPrimary: colorBlackberry,
    colorPrimarySoft: colorBlackberry60,
    colorSecondary: colorPomegranate,
    colorWarning: colorMango,
    colorAlert: colorPomegranate,
  },
  bannerSection: {
    colorBackground: colorCoconut
  },
  button: {
    primary: {
      boxShadow: '0px 10px 20px 0px rgba(0,0,0,0.24)',
      padding: '2.125em 2.125em 1.765em',
      colorBackground: colorPomegranate,
      colorBackgroundHover: '#c8000f',
      colorBackgroundFocus: '#c8000f',
      colorBackgroundDisabled: colorPomegranate40,
      colorBorder: colorPomegranate,
      colorBorderHover: '#c8000f',
      colorBorderDisabled: colorPomegranate40,
      colorText: colorWhite,
      colorTextHover: colorCoconut,
      colorTextDisabled: colorCoconut,
    },
    secondary: {
      boxShadow: 'none',
      padding: '1.375em 1.375em 1.125em',
      colorBackground: 'transparent',
      colorBackgroundHover: colorBlackberry20,
      colorBackgroundFocus: colorBlackberry20,
      colorBackgroundDisabled: 'transparent',
      colorBorder: colorBlackberry,
      colorBorderHover: colorBlackberry,
      colorBorderDisabled: colorBlackberry20,
      colorText: colorBlackberry,
      colorTextHover: colorBlackberry,
      colorTextDisabled: colorBlackberry20,
    },
    alert: {
      boxShadow: 'none',
      padding: '1.375em 1.375em 1.125em',
      colorBackground: 'transparent',
      colorBackgroundHover: colorBlackberry20,
      colorBackgroundFocus: colorBlackberry20,
      colorBackgroundDisabled: 'transparent',
      colorBorder: colorBlackberry,
      colorBorderHover: colorBlackberry,
      colorBorderDisabled: colorBlackberry20,
      colorText: colorBlackberry,
      colorTextHover: colorBlackberry,
      colorTextDisabled: colorBlackberry20,
    },
    baseButtonMinWidth: '11.25em',
    letterSpacing: '2px',
    fontWeight: '700',
    fontSize: '1.4rem',
    textTransform: 'uppercase',
    borderRadius: '0',
    borderWidth: '2px',
    tinySize: '1.2rem',
    tinyPadding: '0.75em 2em 0.5em',
  },
  card: {
    borderRadius: '0',
    borderWidth: '2px',
    colorBorder: 'transparent',
    colorBackground: colorWhite,
    boxShadow: `1px 2px 7px 1px rgba(30, 30, 30, 0.05)`,
  },
  checkbox: {
    colorBorder: colorBlackberry70,
    colorBackground: 'transparent',
    colorBackgroundChecked: colorAvocado,
    colorBackgroundAlert: 'transparent',
    fontSize: '1.6rem'
  },
  datepicker: {
    ...merge(cloneDeep(defaultDatePicker), {
      weekday: {
        color: 'inherit'
      },
      navButton: {
        color: colorPomegranate,
        // tslint:disable:max-line-length
        nextIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' %3E%3Cpath fill='${encodeURIComponent(color)}' d='M8 0L6.59 1.41 12.17 7H0v2h12.17l-5.58 5.59L8 16l8-8z' fillRule='nonzero' /%3E%3C/svg%3E"`,
        prevIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' %3E%3Cpath fill='${encodeURIComponent(color)}' d='M8 0l1.41 1.41L3.83 7H16v2H3.83l5.58 5.59L8 16 0 8z' fillRule='nonzero' /%3E%3C/svg%3E"`,
        // tslint:enable:max-line-length
        backgroundSize: '100%'
      },
      selection: {
        color: colorBlackberry,
        backgroundColor: colorBlackberry20,
      }
    })
  },
  header: {
    colorBreadcrumbText: colorBlackberry60,
    colorHeaderDrawerLinkAboveSmallBP: colorBlackberry,
    colorLink: colorBlackberry,
    colorLinkHover: colorBlackberry,
    colorSubNavLink: colorBlackberry,
    colorSubNavLinkHover: colorBlackberry,
    subNavBreakPoint: 'medium',
    summaryWeight: '700',
  },
  headline: {
    small: {
      size: '1.8rem',
      sizeResponsive: '1.8rem',
      lineHeightResponsive: '2.4rem',
      letterSpacing: 'normal',
      lineHeight: '2.4rem',
      weight: '700',
      colorText: colorBlackberry,
      displayTypeface: 'sailec'
    },
    medium: {
      size: '2.2rem',
      sizeResponsive: '2.2rem',
      lineHeight: '3.2rem',
      lineHeightResponsive: '3.2rem',
      letterSpacing: 'normal',
      weight: '400',
      colorText: colorBlackberry,
      displayTypeface: 'sailec'
    },
    large: {
      size: '2.8rem',
      sizeResponsive: '2.8rem',
      lineHeight: '4.0rem',
      lineHeightResponsive: '4.0rem',
      letterSpacing: 'normal',
      weight: '400',
      colorText: colorBlackberry,
      displayTypeface: 'sailec'
    }
  },
  icon: {
    brandText: {
      colorIcon: colorBlackberry,
    },
    brand: {
      colorIcon: colorBlackberry,
    },
    link: {
      colorIcon: colorBlackberry,
    },
    grey: {
      colorIcon: colorCoconut,
    },
    secondary: {
      colorIcon: colorAvocado,
    },
    tertiary: {
      colorIcon: colorAvocado,
    },
    error: {
      colorIcon: colorPomegranate,
    },
  },
  input: {
    boxShadowHover: 'none',
    boxShadowAlert: 'none',
    colorBorder: colorBlackberry60,
    colorBackground: colorWhite,
    colorPlaceholder: colorBlackberry60,
    colorFocus: colorBlackberry,
    colorTextAreaBorder: colorBlackberry60,
    colorTextAreaBackground: colorWhite,
    borderRadius: '0',
    borderWidth: '2px',
    padding: '0.75em 0em 0.5em 0em',
    // tslint:disable-next-line:max-line-length
    selectInputBackgroundSvgFocused: `"data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIC03IDE3IDE3IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Im0xNiAxLTcuNSA3LTcuNS03IiBmaWxsPSJub25lIiBzdHJva2U9IiMwMjJhM2EiIHN0cm9rZS13aWR0aD0iMiIvPjwvc3ZnPg=="`,
    textAreaPadding: '1em',
  },
  label: {
    fontWeight: '700',
    size: '1.4rem',
    spacing: '2px',
    textTransform: 'uppercase',
    datapointWeight: 'bold',
    formLabelMargin: '0',
    formLabelWeight: '300',
    formLabelColor: colorBlackberry60,
    smallTypeWeight: '700',
    cardLabelSize: '1.6rem',
  },
  link: {
    colorLink: colorBlackberry,
    colorLinkIcon: colorBlackberry,
    colorLinkHover: colorBlackberry70,
  },
  modal: {
    ...defaultModal,
    mobileTextAlign: 'left',
    headlineTextAlign: 'left',
    padding: '3em',
    colorBoxShadow: `none`,
    colorText: colorBlackberry,
    colorHeadlineText: colorBlackberry,
    colorBackground: 'rgba(0,0,0,0.85)',
    colorDrawerBorder: colorBlackberry05,
    colorDrawerHover: colorBlackberry,
    colorDrawerBackgroundHover: colorBlackberry05,
    colorClose: colorBlackberry70,
    borderRadius: '0',
  },
  notification: {
    success: {
      colorBorder: colorAvocado
    },
    error: {
      colorBorder: colorPomegranate
    },
    info: {
      colorBorder: colorBlackberry70
    },
    warning: {
      colorBorder: colorMango
    },
    widthBorder: '0.5em',
    spacing: '1em',
    colorHover: colorBlackberry45
  },
  pill: {
    alert: {
      colorBackground: colorPomegranate,
      colorText: colorCoconut50
    },
    success: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
    },
    primary: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
    },
    secondary: {
      colorBackground: colorBlackberry80,
      colorText: colorCoconut50,
    },
    warning: {
      colorBackground: colorMango,
      colorText: colorBlackberry
    },
    inactive: {
      colorBackground: colorBlackberry70,
      colorText: colorWhite
    },
    default: {
      colorBackground: colorAvocado,
      colorText: colorBlackberry,
    },
    letterSpacing: '0',
    padding: '0.4em 0.7em 0',
    borderRadius: '2px',
    textTransform: 'uppercase',
    fontSize: '1rem',
  },
  progress: {
    colorBar: colorBlackberry,
    colorCircularBackground: colorCoconut50,
    colorBarBackground: colorCoconut50,
    colorBarBorder: 'transparent',
    circularWeight: '700',
    circularSize: '3.6rem',
  },
  progressTracker: {
    colorCompleted: colorAvocado,
    colorOutstanding: colorSlate10,
    colorProgressTrackerBackground: 'transparent',
    colorTextOutstanding: colorAvocado,
    colorTextCompleted: colorAvocado,
    colorStepBgCompleted: colorAvocado,
    colorStepBorderCompleted: colorAvocado,
    colorStepBgOutstanding: 'transparent',
    colorStepBorderOutstanding: colorAvocado,
    colorHrOutstanding: colorAvocado,
    colorHrCompleted: colorAvocado,
    headerContainerPadding: '1em',
    hrHorizontalOffset: '11px',
    hrVerticalOffset: '-31.5px',
    hrWidth: '88px',
    iconCheckedDone: 'progress-done',
    stepLabelFontWeight: '500',
    stepLabelHorizontalOffset: '1em',
    stepNumberFontWeight: '700',
    stepNumberFontSize: '1.4rem',
    stepNumberVerticalOffset: '0',
    svgCompletedHeight: '10px',
    svgCompletedWidth: '10px',
    trackerIndicatorWidth: '24px',
    trackerIndicatorHeight: '24px',
    trackerWrapperWidth: '150px',
  },
  radio: {
    boxShadow: '1px 2px 7px 1px rgba(30,30,30,0.05)',
    boxShadowHover: '1px 2px 7px 1px rgba(30,30,30,0.15)',
    colorBorder: colorBlackberry70,
    colorBorderHover: colorBlackberry,
    colorBorderChecked: 'transparent',
    colorText: colorBlackberry70,
    colorTextHover: colorBlackberry,
    colorTextAlert: colorBlackberry70,
    colorTextChecked: colorWhite,
    colorBackground: 'transparent',
    colorBackgroundHover: 'transparent',
    colorBackgroundChecked: colorBlackberry,
    padding: '0.875em 1.25em',
    borderWidth: '2px',
    fontWeight: '400',
    focus: {
      outline: { color: colorAvocado, offset: '2px' },
      border: { size: '2px', color: colorBlackberry70 },
      colorText: colorBlackberry70
    },
    disabled: {
      labelColor: colorBlackberry45,
      border: { size: '2px', color: colorBlackberry45 },
      colorBackground: colorCoconut
    },
    disabledChecked: {
      colorBackground: colorBlackberry20,
      border: { size: '2px', color: colorBlackberry20 },
      labelColor: colorWhite
    },
    alertChecked: {
      colorText: colorBlackberry70,
      colorBackground: 'transparent',
      border: { size: '2px', color: colorPomegranate }
    }
  },
  radioCard: {
    colorText: colorBlackberry,
    colorTextHover: colorBlackberry,
    colorBackgroundChecked: 'rgba(123,239,178,0.2)',
    border: { size: '2px', color: 'transparent' },
    checked: {
      border: { size: '2px', color: colorAvocado }
    },
    hover: {
      border: { size: '2px', color: 'transparent' }
    }
  },
  radioInput: {
    colorInner: colorBlackberry,
    borderWidth: '2px',
    borderWidthActive: '2px',
    hasActiveHeightAndWidth: false,
    colorBackground: 'transparent',
    colorBorder: colorBlackberry,
    colorBorderInactive: colorBlackberry70,
    colorRadioLabelBackground: 'transparent',
    colorRadioLabelBackgroundActive: 'transparent',
    colorRadioButtonHover: 'transparent',
    sizeRadioButton: '1.125em',
    sizeRadioButtonHover: '1.125em',
    sizeRadioButtonInnerMark: '0.625em',
    positionRadioButtonInnerMarkTop: '0.5em',
    positionRadioButtonInnerMarkLeft: '0.5em',
    labelTextOffset: '0.75em',
  },
  spinner: {
    colorBackground: colorBlackberry20,
    colorSegment: colorBlackberry
  },
  table: {
    alert: {
      colorIcon: colorBlackberry,
      colorText: colorBlackberry,
    },
    success: {
      colorIcon: colorAvocado,
      colorText: colorAvocado,
    },
    primary: {
      colorIcon: colorBlackberry,
      colorText: colorBlackberry,
    },
    secondary: {
      colorIcon: colorAvocado,
      colorText: colorBlackberry,
    },
    warning: {
      colorIcon: colorMango,
      colorText: colorBlackberry,
    },
    inactive: {
      colorIcon: colorBlackberry70,
      colorText: colorBlackberry70,
    },
    default: {
      colorIcon: colorBlackberry,
      colorText: colorBlackberry,
    },
    filter: {
      colorText: colorBlackberry,
      colorTextSelected: colorBlackberry,
      colorBackground: tint(tableFilterBackgroundTint, colorBlackberry),
      borderRadius: '4px',
    },
    header: {
      colorBackground: colorBlackberry05,
      colorSubHeaderBackground: tint(headerBackgroundTint, colorBlackberry),
      fontWeight: '700',
    },
    colorDetailsButton: colorBlackberry,
    colorDivider: colorBlackberry20,
  },
  taskCard: {
    colorBorder: colorBlackberry,
    colorText: colorBlackberry,
    colorIcon: colorBlackberry,
    colorCtaIcon: colorBlackberry,
    colorSubhead: colorBlackberry,
    colorInReviewBorder: colorBlackberry,
    colorCompleteText: colorBlackberry60,
    fontWeightHeadline: '700',
    fontWeightSubHead: '400',
    hasIcon: true,
  },
  text: {
    colorText: colorBlackberry,
    colorCaption: colorBlackberry70,
  },
  toolTip: {
    paneFontSize: '1.6rem',
    paneBackgroundColor: colorWhite,
    paneBoxShadow: `0 0 12px ${colorSlate10}`,
    paneBorderRadius: '0.25em',
  },
  typography: {
    bodyTypeface: 'sailec',
    displayTypeface: 'sailec',
  },
  upload: {
    colorBackground: 'rgba(255,255,255,0.95)',
    colorBorder: 'rgba(123,239,178,0.8)',
    colorActiveBackground: 'rgba(255,255,255,0.95)',
    colorActiveBorder: 'rgba(123,239,178,0.8)',
    colorText: colorBlackberry,
    borderStyle: 'solid',
    borderWidth: '2px',
    size: '20em',
  },
  verifications: {
    mastHeadlineColor: colorBlackberry,
  },
   // themeTwo should be removed once themeTwo replaces current theme
   themeTwo: { ...amountThemeTwo },
};
